import React from 'react';
import { CircleDot, Disc3, Info, Ruler, Sparkles } from 'lucide-react';
import { Chips } from './TyreFormControls';
import EuLabelFilters from './EuLabelFilters';
import { seasonOptions } from './TyreSearchForm';
import { useT } from '../../services/i18n';

const SizeButton = ({ label, count, active, onClick, testId, availKey, t }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
    className={`flex flex-col items-start rounded-xl border px-4 py-3 text-left transition-colors ${active ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white hover:border-orange-300'}`}>
    <span className="text-base font-bold text-slate-900 tracking-tight">{label}</span>
    <span className={`text-xs ${count ? 'text-green-700' : 'text-slate-400'}`}>{count ? t(availKey, { n: count, count }) : t('tw.vf.none')}</span>
  </button>
);

const VehicleFitment = ({ fitment, selection, onSelect, onSearch, onSwitchTab }) => {
  const { t } = useT();
  const v = fitment.vehicle;
  const title = `${v.brand} ${v.model}${v.year ? ` (${v.year})` : ''}${v.engine ? ` · ${v.engine}` : ''}`;
  if (!fitment.found) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center" data-testid="vehicle-fitment-empty">
        <Info size={28} className="mx-auto text-slate-400 mb-2" />
        <p className="font-semibold text-slate-800">{t('tw.vf.notFound', { v: title })}</p>
        <p className="text-sm text-slate-500 mt-1">{t('tw.vf.notFoundHint')}</p>
        <button type="button" onClick={onSwitchTab} data-testid="vehicle-fitment-switch" className="mt-3 text-sm font-semibold text-orange-600 hover:underline">{t('tw.vf.switch')}</button>
      </div>
    );
  }
  const bolt = fitment.bolt_pattern || {};
  const inSize = (s) => (s ? t('tw.vf.in', { s }) : '');
  return (
    <div className="space-y-5" data-testid="vehicle-fitment">
      <div className="flex items-center gap-2 text-sm text-slate-700"><Ruler size={16} className="text-orange-500" /> {t('tw.vf.oeFor')} <strong data-testid="vehicle-fitment-title">{title}</strong></div>
      {fitment.ai_unverified && (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="vehicle-fitment-ai-notice">
          <Sparkles size={14} className="mt-0.5 shrink-0" /> {t('tw.vf.aiNotice')}
        </p>
      )}
      <section className="rounded-xl border border-slate-200 p-4">
        <h3 className="flex items-center gap-2 font-bold text-slate-900 mb-3"><CircleDot size={18} className="text-orange-500" /> {t('tw.vf.oeTyres')}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {fitment.tyre_sizes.map((s) => <SizeButton key={s.label} label={s.label} count={s.count} availKey="tw.vf.tyresAvail" t={t} active={selection.size === s.label} onClick={() => onSelect({ size: selection.size === s.label ? '' : s.label })} testId={`vt-tyre-size-${s.width}${s.height}${s.diameter}`} />)}
        </div>
        <div className="mt-3"><EuLabelFilters value={selection} onChange={(x) => onSelect({ fuel: x.fuel, wet: x.wet, noise_max: x.noise_max, snowflake: x.snowflake })} withSnowflake testIdPrefix="vt-eu" /></div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Chips value={selection.season} onChange={(season) => onSelect({ season })} options={seasonOptions(t)} testIdPrefix="vt-season" />
          <button type="button" onClick={() => onSearch('tyre')} data-testid="vt-search-tyres" className="ml-auto inline-flex items-center h-11 px-6 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm">
            {t('tw.vf.seeTyres')} {selection.size ? inSize(selection.size) : `(${fitment.tyre_sizes.reduce((n, s) => n + s.count, 0)})`}
          </button>
        </div>
      </section>
      {(fitment.wheel_sizes.length > 0 || bolt.label) && (
        <section className="rounded-xl border border-slate-200 p-4">
          <h3 className="flex items-center gap-2 font-bold text-slate-900 mb-3"><Disc3 size={18} className="text-sky-600" /> {t('tw.vf.oeWheels')}</h3>
          {bolt.label && (
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm mb-3" data-testid="vt-bolt-pattern">
              <div className="rounded-lg bg-slate-50 p-2"><dt className="text-[11px] uppercase text-slate-500">{t('tw.pcdShort')}</dt><dd className="font-semibold">{bolt.label}</dd></div>
              {bolt.center_bore && <div className="rounded-lg bg-slate-50 p-2"><dt className="text-[11px] uppercase text-slate-500">{t('tw.bore')}</dt><dd className="font-semibold">{String(bolt.center_bore).replace('.', ',')} mm</dd></div>}
              {bolt.nut_thread && <div className="rounded-lg bg-slate-50 p-2"><dt className="text-[11px] uppercase text-slate-500">{t('tw.vf.thread')}</dt><dd className="font-semibold">{bolt.nut_thread}</dd></div>}
              <div className="rounded-lg bg-slate-50 p-2"><dt className="text-[11px] uppercase text-slate-500">{t('tw.vf.diameters')}</dt><dd className="font-semibold">{[...new Set(fitment.wheel_sizes.map((s) => `${s.diameter}"`))].join(', ') || '—'}</dd></div>
            </dl>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {fitment.wheel_sizes.map((s) => <SizeButton key={s.label} label={s.label} count={s.count} availKey="tw.vf.wheelsAvail" t={t} active={selection.wheelSize === s.label} onClick={() => onSelect({ wheelSize: selection.wheelSize === s.label ? '' : s.label })} testId={`vt-wheel-size-${String(s.width).replace('.', '')}${s.diameter}`} />)}
          </div>
          <div className="mt-3 flex justify-end">
            <button type="button" onClick={() => onSearch('wheel')} data-testid="vt-search-wheels" className="inline-flex items-center h-11 px-6 rounded-lg bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm">
              {t('tw.vf.seeWheels')} {selection.wheelSize ? inSize(selection.wheelSize) : `(${fitment.wheels_total})`}
            </button>
          </div>
        </section>
      )}
      {fitment.notes?.length > 0 && <p className="text-xs text-slate-500 flex items-start gap-1"><Info size={14} className="mt-0.5 shrink-0" /> {fitment.notes[0]}</p>}
    </div>
  );
};

export default VehicleFitment;
